/** @jsx jsx */

import { useCallback, useState } from 'react';

import LogoZapierPlatform from '@zapier/product-dev-platform/src/components/LogoZapierPlatform';
import { HeaderUser } from '@zapier/product-dev-platform/src/types';
import { Colors, Typography } from '@zapier/theme';
import { Icon, SkipNav } from '@zapier/common-components';
import {
  LOGIN_URL,
  MEDIA_MIN_DESKTOP,
  navLinkBaseCss,
} from '@zapier/product-dev-platform/src/constants';
import { css, jsx } from '@emotion/core';

import CtaButtons from './CtaButtons';
import CtaLinks from './CtaLinks';
import Nav from './Nav';
import UserOptions from './UserOptions';
import { HEADER_NAV_USER_LINKS, CTA_LINKS, USER_LINKS } from './constants';

import type { NavItem } from './types';

const Styles = {
  root: {
    backgroundColor: Colors.blackWidow,
    ...Typography.body,
    /** This 1px is taken into account in property top of rightSplitCss. */
    borderBottom: 'solid 1px rgba(255, 255, 255, 0.1)',
    color: Colors.adamantium,
    padding: '0 30px',
  },
  container: {
    alignItems: 'center',
    display: 'flex',
    height: 55,
    justifyContent: 'space-between',
    margin: 'auto',
    position: 'relative' as const,
    [MEDIA_MIN_DESKTOP]: {
      height: 68,
    },
  },
  rightSplit: (isNavOpen: boolean) => ({
    backgroundColor: Colors.white,
    display: isNavOpen ? 'flex' : 'none',
    flexDirection: 'column' as const,
    left: -20,
    position: 'absolute' as const,
    right: -20,
    /** This 1px is an offset to property bottom-border of wrapperCss. */
    top: 'calc(100% + 1px)',
    zIndex: 1,
    [MEDIA_MIN_DESKTOP]: {
      alignItems: 'center',
      alignSelf: 'stretch',
      backgroundColor: 'transparent',
      display: 'flex',
      flexDirection: 'row' as const,
      flex: '1 0 auto',
      left: 'auto',
      marginLeft: 40,
      position: 'relative' as const,
      right: 'auto',
      top: 'auto',
    },
  }),
  onlyMobile: {
    [MEDIA_MIN_DESKTOP]: {
      display: 'none',
    },
  },
  logo: {
    display: 'flex',
    left: '50%',
    position: 'absolute' as const,
    transform: 'translateX(-50%)',
    [MEDIA_MIN_DESKTOP]: {
      position: 'static' as const,
      transform: 'none',
    },
  },
};

type Props = {
  /** Links that appear on the right side of the header */
  ctaLinks?: Array<NavItem>;
  /** The curent URL of the page. */
  currentUrl: string;
  /** Link to be used in the logo image */
  logoUrl?: string;
  /** Main links that appear on the left side of the header */
  navLinks?: Array<NavItem>;
  /** Callback when clicking to logout */
  onClickLogout?: () => void;
  /** Callback when clicking to go to zapier.com */
  onClickZapier?: () => void;
  /** Callback when user menu opens */
  onOpenUserOptions?: () => void;
  /** Location of the website url */
  urlPath?: string;
  /** The current user's name and avatarUrl */
  user?: HeaderUser;
  /** Links that appear when clicking on the user's avatar */
  userLinks?: Array<NavItem>;
};

/**
 * Platform header.
 *
 * *Note: for best results view this component in fullpage mode.*
 */
function Header({
  currentUrl,
  ctaLinks = CTA_LINKS,
  logoUrl,
  navLinks = HEADER_NAV_USER_LINKS,
  onClickLogout,
  onClickZapier,
  onOpenUserOptions,
  user,
  userLinks = USER_LINKS,
  urlPath,
}: Props) {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isUserOptionsOpen, setUserOptionsOpen] = useState(false);
  const toggleNav = useCallback(() => setIsNavOpen(val => !val), []);
  const onToggleUserOptions = useCallback(() => {
    setUserOptionsOpen(val => {
      if (!val && onOpenUserOptions) {
        onOpenUserOptions();
      }
      return !val;
    });
  }, [onOpenUserOptions]);
  const activateLink = (links: typeof HEADER_NAV_USER_LINKS) =>
    links.map(link =>
      link.href === currentUrl ? { ...link, isActive: true } : link
    );
  const ctaLinksWithActivation = activateLink(ctaLinks);
  const navLinksWithActivation = activateLink(navLinks);

  return (
    <header css={Styles.root}>
      <div css={Styles.container}>
        <Icon
          css={Styles.onlyMobile}
          name={isNavOpen ? 'delete' : 'menu'}
          onClick={toggleNav}
        />
        <a
          aria-label="Zapier Platform"
          css={Styles.logo}
          href={logoUrl}
          tabIndex={0}
        >
          <LogoZapierPlatform theme="dark" width={192} />
        </a>
        <SkipNav
          ariaLabel="Skip to main content"
          skipToSelector={'main'}
          urlPath={urlPath}
        />
        <div css={Styles.rightSplit(isNavOpen)}>
          <Nav links={navLinksWithActivation} />
          {user ? <CtaLinks links={ctaLinksWithActivation} /> : <CtaButtons />}
        </div>
        {user ? (
          <UserOptions
            ctaLinks={ctaLinksWithActivation}
            isOpen={isUserOptionsOpen}
            onClickLogout={onClickLogout}
            onClickZapier={onClickZapier}
            onToggle={onToggleUserOptions}
            optionsLinks={userLinks}
            user={user}
          />
        ) : (
          <a css={css(Styles.onlyMobile, navLinkBaseCss)} href={LOGIN_URL}>
            Log In
          </a>
        )}
      </div>
    </header>
  );
}

export default Header;
